"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";

export type ColourVariantPanelItem = {
    id: string;
    name: string;
    price: string;
    href?: string;
};

type ColourVariantPanelProps = {
    /** Variants offered in the selected colour. */
    variants: ColourVariantPanelItem[];
    /** Every variant of the model, for the "X of Y variants" title. */
    totalCount: number;
    /** Changes whenever the selected colour changes, so the list scrolls back to the top. */
    resetKey?: string | number;
    onNavigate?: () => void;
};

const arrowClass = (enabled: boolean) =>
    `flex h-9 w-9 items-center justify-center rounded-full ${
        enabled ? "cursor-pointer bg-white" : "cursor-not-allowed bg-[#3d3d3d]"
    }`;

/**
 * Desktop variant card list shown beside the colour carousel. Shared by the
 * colours-page gallery and the images-page lightbox so both look identical.
 */
export default function ColourVariantPanel({
    variants,
    totalCount,
    resetKey,
    onNavigate,
}: ColourVariantPanelProps) {
    const [scrollIndex, setScrollIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setScrollIndex(0);
    }, [resetKey, variants.length]);

    useEffect(() => {
        const target = listRef.current?.querySelector<HTMLElement>(
            `[data-variant-scroll-index="${scrollIndex}"]`,
        );
        target?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [scrollIndex]);

    if (!variants.length) return null;

    const canGoPrev = scrollIndex > 0;
    const canGoNext = scrollIndex < variants.length - 1;

    const step = (direction: 1 | -1) => {
        setScrollIndex((index) => {
            const next = index + direction;
            if (next < 0 || next >= variants.length) return index;
            return next;
        });
    };

    return (
        <div className="hidden h-[450px] w-[180px] shrink-0 items-start gap-3 md:flex">
            <div className="flex h-[422px] flex-col items-center justify-center gap-3 pt-6">
                <button
                    type="button"
                    aria-label="Previous variant"
                    disabled={!canGoPrev}
                    onClick={() => step(-1)}
                    className={arrowClass(canGoPrev)}
                >
                    <svg width="13" height="7" viewBox="0 0 13 7" fill="none" aria-hidden>
                        <path
                            d="M1 6L6.35714 1L11.7143 6"
                            stroke={canGoPrev ? "#111111" : "#7a7a7a"}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <button
                    type="button"
                    aria-label="Next variant"
                    disabled={!canGoNext}
                    onClick={() => step(1)}
                    className={arrowClass(canGoNext)}
                >
                    <svg width="13" height="7" viewBox="0 0 13 7" fill="none" aria-hidden>
                        <path
                            d="M11.7142 1L6.35709 6L0.999948 1"
                            stroke={canGoNext ? "#111111" : "#7a7a7a"}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
            <div className="min-w-0 flex-1">
                <p className="mb-2 text-center text-[11px] text-white/50">
                    {variants.length} of {totalCount} variants
                </p>
                <div className="h-[422px] rounded-2xl bg-[#2a2a2a] p-2">
                    <div ref={listRef} className="h-full space-y-2 overflow-y-auto scrollbar-hide">
                        {variants.map((variant, index) => {
                            const className =
                                "block w-full rounded-xl border border-white/30 bg-[#2a2a2a] px-3 py-2.5 text-left text-white";
                            const body = (
                                <>
                                    <p className="truncate text-[13px] font-medium">{variant.name}</p>
                                    <p className="mt-1 text-[12px] text-white/70">{variant.price}</p>
                                </>
                            );
                            if (variant.href) {
                                return (
                                    <Link
                                        key={variant.id}
                                        data-variant-scroll-index={index}
                                        href={variant.href}
                                        prefetch={false}
                                        onClick={onNavigate}
                                        className={className}
                                    >
                                        {body}
                                    </Link>
                                );
                            }
                            return (
                                <div key={variant.id} data-variant-scroll-index={index} className={className}>
                                    {body}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
